import React from 'react';
import ReactDOM from 'react-dom';

import LayerTrigger from 'components/LayerTrigger.jsx';
import findScrollParent from 'utils/findScrollParent';
import createChainedFunction from 'utils/createChainedFunction';

// TODO: Keyboard accessibility. Should focus the layer and use tab isolation.

let _currentInstanceShown = null;
let _showTimeout = null;

/**
 * LayerTrigger with a specific hover behavior for use with Hovercard.
 *
 * HoverLayerTrigger was built to be used with [Hovercard](/components/Hovercard). It is a
 * [LayerTrigger](/components/LayerTrigger) with a special customizable delayed-hover behavior.
 *
 * Unlike a normal LayerTrigger, the user is able to move their mouse from the HoverLayerTrigger
 * element to the layer. The layer is hidden when the user moves their mouse away from the layer
 * or performs an action in the layer.
 *
 * If the user hovers over another HoverLayerTrigger while moving their mouse towards a visible
 * layer, that HoverLayerTrigger will NOT be triggered.
 *
 * ```example
 *  <div>
 *    <HoverLayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>
 *            You can move your mouse here!
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      placement="top">
 *      <Link block="inline" style={{border: '1px solid #eee', padding: 20}}>
 *        Hover me
 *      </Link>
 *    </HoverLayerTrigger>
 *    <HoverLayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>
 *            You can move your mouse here!
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      placement="top">
 *      <Link block="inline" style={{border: '1px solid #eee', padding: 20}}>
 *        Hover me
 *      </Link>
 *    </HoverLayerTrigger>
 *    <HoverLayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>
 *            You can move your mouse here!
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      placement="top">
 *      <Link block="inline" style={{border: '1px solid #eee', padding: 20}}>
 *        Hover me
 *      </Link>
 *    </HoverLayerTrigger>
 *    <HoverLayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>
 *            You can move your mouse here!
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      placement="top">
 *      <Link block="inline" style={{border: '#eee', padding: 20}}>
 *        Hover me
 *      </Link>
 *    </HoverLayerTrigger>
 *  </div>
 * ```
 */

const HoverLayerTrigger = React.createClass({
  propTypes: {
    ...LayerTrigger.propTypes,
    layer: React.PropTypes.node.isRequired,
    delay: React.PropTypes.number,
    delayShow: React.PropTypes.number,
    delayHide: React.PropTypes.number,
  },
  componentDidMount() {
    this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
  },

  _mouseOrigin: null,

  _scrollParent: null,

  addDocumentListeners() {
    document.addEventListener('mousemove', this.handleDocumentMouseMove, false);
  },

  handleClick() {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    const { trigger } = this.refs;
    if (trigger.isLayerShown()) {
      trigger.hideLayer();
      _currentInstanceShown = null;
    }
  },

  handleDelayedHide() {
    if (this._hideTimeout !== null) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }

    const delay = this.props.delayHide !== null ?
      this.props.delayHide : this.props.delay;

    if (!delay) {
      this.hideLayer();
      return;
    }

    this._hideTimeout = setTimeout(() => {
      this._hideTimeout = null;
      this.hideLayer();
    }, delay);
  },

  handleDelayedShow() {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    const delay = this.props.delayShow !== null ?
      this.props.delayShow : this.props.delay;

    if (!delay) {
      this.showLayer();
      return;
    }

    _showTimeout = setTimeout(() => {
      _showTimeout = null;
      this.showLayer();
    }, delay);
  },

  handleDocumentMouseMove(event) {
    if (this.shouldHideLayer({ x: event.clientX, y: event.clientY })) {
      this.handleDelayedHide();
      this.removeDocumentListeners();
    }
  },

  handleDocumentScroll() {
    this.handleDelayedHide();
    this.removeDocumentListeners();
  },

  handleLayerHide() {
    this.removeDocumentListeners();
    _currentInstanceShown = null;
  },

  handleLayerMouseEnter() {
    this.removeDocumentListeners();
  },

  handleLayerMouseLeave() {
    this.handleDelayedHide();
  },

  handleMouseEnter() {
    this.handleDelayedShow();
  },

  handleMouseLeave(event) {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    if (_currentInstanceShown !== this) {
      return;
    }

    this._mouseOrigin = {
      x: event.clientX,
      y: event.clientY,
    };

    this.addDocumentListeners();
  },

  handleMouseMove() {
    this.handleDelayedShow();
  },

  hideLayer() {
    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.handleDocumentScroll, false);
    }

    if (this.isMounted()) {
      this.refs.trigger.hideLayer();
    }
    _currentInstanceShown = null;
  },

  isPointInTriangle(point, p1, p2, p3) {
    // This checks to see if the mouse is moving within a triangle formed by our three points.
    // See: http://www.blackpawn.com/texts/pointinpoly/default.html
    // This would be better if the sides of the triangle weren't straight and had a parabolic-like
    // shape, but that just seemed really hard.
    // I might just adjust the origin by some amount to provide some leeway.

    const v0 = [p3.x - p1.x, p3.y - p1.y];
    const v1 = [p2.x - p1.x, p2.y - p1.y];
    const v2 = [point.x - p1.x, point.y - p1.y];

    const dot00 = (v0[0] * v0[0]) + (v0[1] * v0[1]);
    const dot01 = (v0[0] * v1[0]) + (v0[1] * v1[1]);
    const dot02 = (v0[0] * v2[0]) + (v0[1] * v2[1]);
    const dot11 = (v1[0] * v1[0]) + (v1[1] * v1[1]);
    const dot12 = (v1[0] * v2[0]) + (v1[1] * v2[1]);

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);

    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    return u >= 0 && v >= 0 && u + v <= 1;
  },

  removeDocumentListeners() {
    document.removeEventListener('mousemove', this.handleDocumentMouseMove, false);
  },

  shouldHideLayer(point) {
    if (!this.isMounted()) {
      return true;
    }

    const { trigger } = this.refs;
    // If the layer is already hidden for some reason, ensure we clean up.
    if (!trigger.isLayerShown()) {
      return true;
    }
    const rect = trigger.getLayerRect();

    // Determine the points on the layer's rect in which the mouse can move towards.
    let p1;
    let p2;
    const origin = this._mouseOrigin;
    if (origin.x > rect.right) {
      // Left
      p1 = {
        x: origin.y < rect.top ? rect.left : rect.right,
        y: rect.top,
      };
      p2 = {
        x: origin.y > rect.bottom ? rect.left : rect.right,
        y: rect.bottom,
      };
    } else if (origin.x < rect.left) {
      // Right
      p1 = {
        x: origin.y < rect.top ? rect.right : rect.left,
        y: rect.top,
      };
      p2 = {
        x: origin.y > rect.bottom ? rect.right : rect.left,
        y: rect.bottom,
      };
    } else if (origin.y > rect.bottom) {
      // Top
      p1 = {
        x: rect.left,
        y: origin.x < rect.left ? rect.top : rect.bottom,
      };
      p2 = {
        x: rect.right,
        y: origin.x > rect.right ? rect.top : rect.bottom,
      };
    } else if (origin.y < rect.top) {
      // Bottom
      p1 = {
        x: rect.left,
        y: origin.x < rect.left ? rect.bottom : rect.top,
      };
      p2 = {
        x: rect.right,
        y: origin.x > rect.right ? rect.bottom : rect.top,
      };
    } else {
      // Is the origin inside of the layer? Not sure how that happened.
      console.warn('origin in layer rect', origin, rect);
      // FIXME https://app.asana.com/0/42965143452863/193556816395307
      console.warn('HoverLayerTrigger non-fatal error');
      return false;
    }

    return !this.isPointInTriangle(point, origin, p1, p2);
  },

  showLayer() {
    // Only allow one layer to be triggered by `HoverLayerTrigger` at a time
    if (_currentInstanceShown !== null) {
      return;
    }

    if (this.isMounted()) {
      if (this._scrollParent) {
        this._scrollParent.addEventListener('scroll', this.handleDocumentScroll, false);
      }

      this.refs.trigger.showLayer();
      _currentInstanceShown = this;
    }
  },

  render() {
    let child = React.Children.only(this.props.children);

    child = React.cloneElement(child, {
      onMouseEnter: createChainedFunction(this.handleMouseEnter, child.props.onMouseEnter),
      onMouseMove: createChainedFunction(this.handleMouseMove, child.props.onMouseMove),
      onMouseLeave: createChainedFunction(this.handleMouseLeave, child.props.onMouseLeave),
      onClick: createChainedFunction(this.handleClick, child.props.onClick),
    });

    let { layer } = this.props;

    layer = React.cloneElement(layer, {
      onMouseEnter: createChainedFunction(this.handleLayerMouseEnter, layer.props.onMouseEnter),
      onMouseLeave: createChainedFunction(this.handleLayerMouseLeave, layer.props.onMouseLeave),
      onRequestHide: createChainedFunction(this.handleLayerHide, layer.props.onRequestHide),
    });

    return (
      <LayerTrigger {...this.props} ref="trigger" layer={layer} trigger="manual">
        {child}
      </LayerTrigger>
    );
  },
});

export default HoverLayerTrigger;



// WEBPACK FOOTER //
// ./src/components/HoverLayerTrigger.jsx